<template>
  <div class="prematch-board">
    <div v-if="tournament" class="prematch-board__tournament">{{ tournament }}</div>

    <div class="prematch-board__teams">
      <div class="prematch-board__team">
        <LogoCompetitors v-if="homeId" :team-id="homeId" height="34px" width="34px" class="prematch-board__logo"/>
        <div class="prematch-board__name text-truncate" :title="homeName">{{ homeName }}</div>
        <FormDots :form="homeForm"/>
      </div>

      <div class="prematch-board__center">
        <div v-if="kickoffTime" class="prematch-board__kickoff">{{ $t('incidents.kickoff') }} {{ kickoffTime }}</div>
        <div class="prematch-board__vs">v</div>
      </div>

      <div class="prematch-board__team">
        <LogoCompetitors v-if="awayId" :team-id="awayId" height="34px" width="34px" class="prematch-board__logo"/>
        <div class="prematch-board__name text-truncate" :title="awayName">{{ awayName }}</div>
        <FormDots :form="awayForm"/>
      </div>
    </div>
  </div>
</template>

<script>
import LogoCompetitors from "@/components/center_widgets/LogoCompetitors.vue";

// competitors[].form: "lost-win-draw-win-draw" or "W-L-W-L-L", most recent first.
const FORM_RESULTS = { w: 'win', win: 'win', won: 'win', d: 'draw', draw: 'draw', l: 'lost', lost: 'lost', loss: 'lost', lose: 'lost' };
const parseForm = form => String(form || '')
    .split(/[-,\s]+/)
    .map(r => FORM_RESULTS[r.trim().toLowerCase()])
    .filter(Boolean);

// Last results as dots (green win, grey draw, red defeat); the latest is underlined.
const FormDots = {
  functional: true,
  props: { form: { type: Array, default: () => [] } },
  render(h, { props }) {
    if (!props.form.length) return null;
    return h('div', { class: 'prematch-form' }, props.form.map((r, i) =>
        h('span', { key: i, class: ['prematch-form__dot', 'is-' + r, { 'is-latest': i === 0 }] })));
  }
};

// Shown over the tracker's pitch before kick-off: tournament, kick-off time,
// the two teams and their recent form.
export default {
  name: "PreMatchBoard",
  components: { LogoCompetitors, FormDots },
  props: {
    matchData: { type: Object, default: null },
    homeName: { type: String, default: '' },
    awayName: { type: String, default: '' }
  },
  computed: {
    homeId() { return this.matchData?.home_id || this.competitor('1')?.id || ''; },
    awayId() { return this.matchData?.away_id || this.competitor('2')?.id || ''; },
    tournament() { return this.matchData?.tournamentName || this.matchData?.tournament?.name || ''; },
    kickoffTime() {
      const d = this.matchData?.scheduled ? new Date(this.matchData.scheduled) : null;
      if (!d || isNaN(d)) return '';
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    },
    homeForm() { return parseForm(this.competitor('1')?.form); },
    awayForm() { return parseForm(this.competitor('2')?.form); }
  },
  methods: {
    competitor(pos) {
      const list = this.matchData?.competitors;
      return Array.isArray(list) ? list.find(c => String(c.position ?? c.Position) === pos) : null;
    }
  }
}
</script>

<style scoped>
.prematch-board {
  position: absolute;
  inset: 0;
  z-index: 30;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 16px;
  color: #fff;
  background: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0.25) 70%, transparent 100%);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
  pointer-events: none;
}

.prematch-board__tournament {
  font-size: var(--font-size-4xl);
  font-weight: 700;
  text-align: center;
}

.prematch-board__teams {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 420px;
}

.prematch-board__team {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.prematch-board__logo {
  width: 34px;
  height: 34px;
  object-fit: contain;
  border-radius: 50%;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.6));
}

.prematch-board__name {
  max-width: 100%;
  font-size: var(--font-size-4xl);
  font-weight: 700;
}

.prematch-board__center {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.prematch-board__kickoff {
  font-size: var(--font-size-2xl);
  opacity: 0.9;
  white-space: nowrap;
}

.prematch-board__vs {
  font-size: var(--font-size-3xl);
  font-weight: 700;
  opacity: 0.8;
}

.prematch-board ::v-deep .prematch-form {
  display: flex;
  gap: 4px;
}

.prematch-board ::v-deep .prematch-form__dot {
  position: relative;
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.prematch-board ::v-deep .prematch-form__dot.is-win { background: #2dd4bf; }
.prematch-board ::v-deep .prematch-form__dot.is-draw { background: #6b7280; }
.prematch-board ::v-deep .prematch-form__dot.is-lost { background: #ef4444; }

.prematch-board ::v-deep .prematch-form__dot.is-latest::after {
  content: '';
  position: absolute;
  left: -1px;
  right: -1px;
  bottom: -4px;
  height: 1.5px;
  border-radius: 1px;
  background: #fff;
}
</style>
